import { Hex } from 'viem'
import { useEnsName } from 'wagmi'
import { sliceAddressForDisplay } from '@utils/common'

/**
 * User data structure for display resolution
 */
export type UserDisplayData = {
  /** Display name from user profile */
  name?: string | null
  /** Username from user profile */
  username?: string | null
  /** Wallet address */
  address?: string | null
}

/**
 * Configuration options for user display resolution
 */
export type UseUserDisplayOptions = {
  /** Whether to fetch ENS name (default: true) */
  includeEns?: boolean
  /** Custom resolution order - if not provided, uses default priority */
  resolutionOrder?: Array<'name' | 'username' | 'ens' | 'address'>
}

/**
 * Hook result containing resolved display information
 */
export type UseUserDisplayResult = {
  /** The resolved display name */
  displayName: string
  /** Whether ENS data is being fetched */
  isLoading: boolean
  /** ENS name if resolved */
  ensName?: string | null
}

/**
 * Default resolution order following the established preference:
 * name → username → ENS → address
 */
const DEFAULT_RESOLUTION_ORDER: UseUserDisplayOptions['resolutionOrder'] = ['name', 'username', 'ens', 'address']

/**
 * Custom hook for resolving user display names with consistent priority order.
 *
 * Follows the established pattern: name → username → ENS → address
 *
 * @param userData - User data object containing profile information
 * @param options - Configuration options for display resolution
 * @returns Resolved display name and loading state
 *
 * @example
 * // Basic usage with token owner data
 * const { displayName, isLoading } = useUserDisplay({
 *   name: token.owner?.profile_by_id?.name,
 *   username: token.owner?.profile_by_id?.username,
 *   address: token.owner_address
 * })
 *
 * @example
 * // Custom resolution order (skip ENS for performance)
 * const { displayName } = useUserDisplay(userData, {
 *   includeEns: false,
 *   resolutionOrder: ['name', 'username', 'address']
 * })
 */
export function useUserDisplay(userData: UserDisplayData, options: UseUserDisplayOptions = {}): UseUserDisplayResult {
  const { includeEns = true, resolutionOrder = DEFAULT_RESOLUTION_ORDER } = options

  // Fetch ENS name if enabled and address is provided
  const { data: ensName, isLoading: ensLoading } = useEnsName({
    address: (userData.address as Hex) ?? undefined,
    query: {
      enabled: includeEns && Boolean(userData.address)
    }
  })

  // Resolve display name based on priority order
  const displayName = resolveDisplayName(
    {
      ...userData,
      ensName
    },
    resolutionOrder as Array<'name' | 'username' | 'ens' | 'address'>
  )

  return {
    displayName,
    isLoading: includeEns ? ensLoading : false,
    ensName
  }
}

/**
 * Resolves display name based on provided resolution order
 */
function resolveDisplayName(
  data: UserDisplayData & { ensName?: string | null },
  resolutionOrder: Array<'name' | 'username' | 'ens' | 'address'>
): string {
  for (const source of resolutionOrder) {
    switch (source) {
      case 'name':
        if (data.name) return data.name
        break
      case 'username':
        if (data.username) return data.username
        break
      case 'ens':
        if (data.ensName) return data.ensName
        break
      case 'address':
        if (data.address) return sliceAddressForDisplay(data.address)
        break
    }
  }

  return 'Unknown User'
}
